import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Switch,
  Button,
  Input,
  Avatar,
  Badge,
  Flex,
  Divider,
  useToast,
  useColorModeValue,
  Tooltip,
  Spinner,
  Checkbox,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
  Icon,
} from '@chakra-ui/react';
import { InfoOutlineIcon, SearchIcon } from '@chakra-ui/icons';
import { FiRotateCcw } from 'react-icons/fi';

/**
 * AppAccessPanel — renders a card for managing a single app's access settings.
 * 
 * Props:
 *  - appId: The appSettings document id (e.g. 'forecast', 'launchpad')
 *  - initConfig: Default config used to seed Firestore when the app is uninitialized
 *  - appConfig: The appSettings document data (with id), or null if uninitialized
 *  - allUsers: Array of FOH users [{uid, name, photo, email, ...}]
 *  - onToggleAllowAll: (appId, currentValue) => Promise
 *  - onToggleUserAccess: (appId, uid, currentAllowedUsers) => Promise
 *  - onTriggerDeploy: (appId) => Promise
 *  - onInitialize: (appId, config) => Promise (for first-time setup)
 *  - onFetchDeployHistory: (appId) => Promise<release[]>
 *  - onRollbackDeploy: (appId, versionName) => Promise
 *  - isSuperAdmin: boolean
 */
export default function AppAccessPanel({
  appId,
  initConfig,
  appConfig,
  allUsers,
  onToggleAllowAll,
  onToggleUserAccess,
  onTriggerDeploy,
  onInitialize,
  onFetchDeployHistory,
  onRollbackDeploy,
  isSuperAdmin,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [deployments, setDeployments] = useState([]);
  const [loadingDeployments, setLoadingDeployments] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const toast = useToast();

  const { isOpen: isRollbackOpen, onOpen: onRollbackOpen, onClose: onRollbackClose } = useDisclosure();
  const cancelRef = React.useRef();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const liveBg = useColorModeValue('green.50', 'green.900');

  // If appConfig is null, this is an uninitialized app — show setup
  const isInitialized = !!appConfig;

  const allowedUsers = appConfig?.allowedUsers || [];
  const allowAll = appConfig?.allowAll || false;

  // Filter users by search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return allUsers;
    const q = searchQuery.toLowerCase();
    return allUsers.filter(user =>
      user.name?.toLowerCase().includes(q) ||
      user.email?.toLowerCase().includes(q)
    );
  }, [allUsers, searchQuery]);

  // Fetch / poll deployments from Firebase Hosting API
  useEffect(() => {
    let interval;
    if (isInitialized && onFetchDeployHistory && isSuperAdmin) {
      const fetchHistory = async () => {
        try {
          if (!deployments || deployments.length === 0) setLoadingDeployments(true);
          const releases = await onFetchDeployHistory(appConfig.id);
          setDeployments(releases || []);
        } catch (error) {
          console.error("Failed to fetch deployment history", error);
        } finally {
          setLoadingDeployments(false);
        }
      };

      fetchHistory();

      // Poll every 30s to keep UI fresh
      interval = setInterval(fetchHistory, 30000);
    }
    return () => clearInterval(interval);
  }, [isInitialized, appConfig?.id, onFetchDeployHistory, isSuperAdmin]);

  const handleToggleAllowAll = async () => {
    try {
      await onToggleAllowAll(appConfig.id, allowAll);
      toast({
        title: allowAll ? 'Allow All disabled' : 'Allow All enabled',
        description: allowAll
          ? 'Only selected users can now access this app'
          : 'All users can now access this app',
        status: allowAll ? 'warning' : 'success',
        position: 'top',
        isClosable: true,
      });
    } catch (err) {
      toast({ title: 'Error: ' + err.message, status: 'error', position: 'top', isClosable: true });
    }
  };

  const handleToggleUser = async (uid, userName) => {
    try {
      const nowAllowed = await onToggleUserAccess(appConfig.id, uid, allowedUsers);
      toast({
        title: nowAllowed
          ? `${userName} granted access`
          : `${userName} access revoked`,
        status: nowAllowed ? 'success' : 'warning',
        position: 'top',
        isClosable: true,
        duration: 2000,
      });
    } catch (err) {
      toast({ title: 'Error: ' + err.message, status: 'error', position: 'top', isClosable: true });
    }
  };

  const handleDeploy = async () => {
    setDeploying(true);
    try {
      const result = await onTriggerDeploy(appConfig.id);
      toast({
        title: 'Deployment triggered! 🚀',
        description: result.message,
        status: 'success',
        position: 'top',
        isClosable: true,
        duration: 5000,
      });
    } catch (err) {
      toast({
        title: 'Deploy failed',
        description: err.message,
        status: 'error',
        position: 'top',
        isClosable: true,
      });
    } finally {
      setDeploying(false);
    }
  };

  const handleRollbackClick = (release) => {
    setRollbackTarget(release);
    onRollbackOpen();
  };

  const handleRollbackConfirm = async () => {
    if (!rollbackTarget || !onRollbackDeploy) return;
    setRollingBack(true);
    onRollbackClose();
    try {
      const result = await onRollbackDeploy(appConfig.id, rollbackTarget.versionName);
      toast({
        title: 'Rollback successful! ⏪',
        description: result.message,
        status: 'success',
        position: 'top',
        isClosable: true,
        duration: 5000,
      });
      // Refresh the release list
      if (onFetchDeployHistory) {
        const releases = await onFetchDeployHistory(appConfig.id);
        setDeployments(releases || []);
      }
    } catch (err) {
      toast({
        title: 'Rollback failed',
        description: err.message,
        status: 'error',
        position: 'top',
        isClosable: true,
      });
    } finally {
      setRollingBack(false);
      setRollbackTarget(null);
    }
  };

  const handleInitialize = async (appId, config) => {
    setInitializing(true);
    try {
      await onInitialize(appId, config);
      toast({
        title: 'App configured!',
        status: 'success',
        position: 'top',
        isClosable: true,
      });
    } catch (err) {
      toast({ title: 'Error: ' + err.message, status: 'error', position: 'top', isClosable: true });
    } finally {
      setInitializing(false);
    }
  };

  // Extract a short version ID from the full version name
  // e.g. "sites/bolt-forecast-1/versions/abc123" → "abc123"
  const getShortVersionId = (versionName) => {
    if (!versionName) return '—';
    const parts = versionName.split('/');
    return parts[parts.length - 1];
  };

  // Format a release time string
  const formatReleaseTime = (releaseTime) => {
    if (!releaseTime) return '—';
    return new Date(releaseTime).toLocaleString();
  };

  // Uninitialized state — show setup button seeded from initConfig
  if (!isInitialized) {
    if (!initConfig) return null;
    return (
      <Box
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={6}
        bg={cardBg}
        maxW="600px"
      >
        <VStack spacing={4} align="center">
          <Text fontSize="lg" fontWeight="bold">{initConfig.appName || appId}</Text>
          <Text color="gray.500" textAlign="center">
            This app hasn't been configured yet. Click below to initialize access control settings.
          </Text>
          <Button
            colorScheme="blue"
            onClick={() => handleInitialize(appId, initConfig)}
            isLoading={initializing}
          >
            Initialize {initConfig.appName || appId}
          </Button>
        </VStack>
      </Box>
    );
  }

  const lastDeployedAt = appConfig.lastDeployedAt?.toDate?.();
  const lastDeployedStr = lastDeployedAt
    ? lastDeployedAt.toLocaleString()
    : 'Never';

  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      overflow="hidden"
      bg={cardBg}
      maxW="800px"
      shadow="sm"
    >
      {/* Header */}
      <Box bg={headerBg} p={4}>
        <Flex justifyContent="space-between" alignItems="center">
          <VStack align="start" spacing={0}>
            <Text fontSize="lg" fontWeight="bold">{appConfig.appName || appConfig.appId}</Text>
            <Text fontSize="sm" color="gray.500">
              {appConfig.repoOwner}/{appConfig.repoName}
            </Text>
          </VStack>
          <Badge colorScheme={allowAll ? 'green' : 'gray'} fontSize="sm" px={2} py={1} borderRadius="md">
            {allowAll ? 'All Users' : `${allowedUsers.length} User${allowedUsers.length !== 1 ? 's' : ''}`}
          </Badge>
        </Flex>
      </Box>

      <VStack spacing={4} p={4} align="stretch">
        {/* Allow All Toggle */}
        <Flex justifyContent="space-between" alignItems="center">
          <HStack>
            <Text fontWeight="medium">Allow All Users</Text>
            <Tooltip label="When enabled, all FOH users can access this app regardless of individual permissions" placement="top">
              <InfoOutlineIcon color="gray.400" />
            </Tooltip>
          </HStack>
          <Switch
            isChecked={allowAll}
            onChange={handleToggleAllowAll}
            colorScheme="green"
            size="lg"
          />
        </Flex>

        <Divider />

        {/* User Permissions */}
        <Box opacity={allowAll ? 0.5 : 1} pointerEvents={allowAll ? 'none' : 'auto'}>
          <Text fontWeight="medium" mb={2}>Individual Access</Text>
          <InputGroup mb={3}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="sm"
            />
          </InputGroup>

          <Box maxH="300px" overflowY="auto" pr={1}>
            <VStack spacing={1} align="stretch">
              {filteredUsers.map((user) => (
                <Flex
                  key={user.uid}
                  alignItems="center"
                  justifyContent="space-between"
                  p={2}
                  borderRadius="md"
                  _hover={{ bg: hoverBg }}
                >
                  <HStack spacing={3}>
                    <Checkbox
                      isChecked={allowedUsers.includes(user.uid)}
                      onChange={() => handleToggleUser(user.uid, user.name)}
                      colorScheme="blue"
                    />
                    <Avatar size="xs" name={user.name} src={user.photo} />
                    <VStack spacing={0} align="start">
                      <Text fontSize="sm" fontWeight="medium">{user.name}</Text>
                      <Text fontSize="xs" color="gray.500">{user.email}</Text>
                    </VStack>
                  </HStack>
                </Flex>
              ))}
            </VStack>
          </Box>
        </Box>

        {/* Deploy & Release History Section */}
        {isSuperAdmin && (
          <>
            <Divider />
            <Box>
              <Flex justifyContent="space-between" alignItems="center" mb={4}>
                <Text fontWeight="medium">Release History</Text>
                <Button
                  colorScheme="purple"
                  size="sm"
                  onClick={handleDeploy}
                  isLoading={deploying}
                  loadingText="Deploying..."
                >
                  🚀 Trigger Deploy
                </Button>
              </Flex>

              {loadingDeployments && deployments.length === 0 ? (
                <Flex justify="center" py={4}><Spinner size="sm" color="purple.500" /></Flex>
              ) : deployments.length === 0 ? (
                <Text fontSize="sm" color="gray.500">No release history found.</Text>
              ) : (
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Status</Th>
                        <Th>Type</Th>
                        <Th>Version</Th>
                        <Th>Date</Th>
                        <Th></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {deployments.slice(0, 10).map((release, idx) => {
                        const isLive = release.isLive;
                        const isRollback = release.type === 'ROLLBACK';

                        return (
                          <Tr key={release.name || idx} bg={isLive ? liveBg : undefined}>
                            <Td py={2} px={2}>
                              {isLive ? (
                                <Badge colorScheme="green" fontSize="xs">● LIVE</Badge>
                              ) : (
                                <Badge colorScheme="gray" fontSize="xs" variant="outline">Previous</Badge>
                              )}
                            </Td>
                            <Td py={2} px={2}>
                              <Badge
                                variant="outline"
                                colorScheme={isRollback ? 'orange' : 'blue'}
                                fontSize="xs"
                              >
                                {isRollback ? '⏪ Rollback' : '🚀 Deploy'}
                              </Badge>
                            </Td>
                            <Td py={2} px={2}>
                              <Tooltip label={release.releaseUser || 'Unknown'} placement="top">
                                <Text fontSize="xs" fontFamily="mono" color="gray.600">
                                  {getShortVersionId(release.versionName)}
                                </Text>
                              </Tooltip>
                            </Td>
                            <Td py={2} px={2} fontSize="xs" color="gray.500" whiteSpace="nowrap">
                              {formatReleaseTime(release.releaseTime)}
                            </Td>
                            <Td py={2} px={2}>
                              {!isLive && onRollbackDeploy && (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="orange"
                                  leftIcon={<Icon as={FiRotateCcw} />}
                                  onClick={() => handleRollbackClick(release)}
                                  isLoading={rollingBack && rollbackTarget?.versionName === release.versionName}
                                  isDisabled={rollingBack}
                                >
                                  Rollback
                                </Button>
                              )}
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Box>
          </>
        )}
      </VStack>

      {/* Rollback Confirmation Dialog */}
      <AlertDialog
        isOpen={isRollbackOpen}
        leastDestructiveRef={cancelRef}
        onClose={onRollbackClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              ⏪ Confirm Rollback
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack spacing={3} align="stretch">
                <Text>
                  Are you sure you want to roll back to this version? This will immediately update the live site.
                </Text>
                {rollbackTarget && (
                  <Box p={3} bg="orange.50" borderRadius="md" borderWidth="1px" borderColor="orange.200">
                    <Text fontSize="sm" fontWeight="bold" color="orange.700">
                      Target Version: {getShortVersionId(rollbackTarget.versionName)}
                    </Text>
                    <Text fontSize="xs" color="orange.600">
                      Originally deployed: {formatReleaseTime(rollbackTarget.releaseTime)}
                    </Text>
                    {rollbackTarget.releaseUser && (
                      <Text fontSize="xs" color="orange.600">
                        By: {rollbackTarget.releaseUser}
                      </Text>
                    )}
                  </Box>
                )}
                <Text fontSize="sm" color="gray.600">
                  This is instant and does not require a rebuild. A new release will be created pointing to the selected version.
                </Text>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onRollbackClose}>
                Cancel
              </Button>
              <Button colorScheme="orange" onClick={handleRollbackConfirm} ml={3}>
                Roll Back
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
